"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SectionData } from "@/lib/types/cms-types";

interface Value {
  id: string;
  number: string;
  title: string;
  description: string;
  image: string;
}

interface ValuesCarouselProps {
  cmsData: {
    [key: string]: SectionData | undefined;
  };
}

// Default values as fallback
const defaultValues: Value[] = [
  {
    id: "embrace-change",
    number: "01",
    title: "Embrace Change",
    description:
      "Changes are inevitable yet necessary. Everyone shall take lead to implement changes in the workplace.",
    image: "/career-page/7values-embrace-change.webp",
  },
  {
    id: "result-driven",
    number: "02",
    title: "Result Driven",
    description:
      "Set specific objectives, separate larger goals into smaller actions, and calculate every possible option to attain them.",
    image: "/career-page/7values-result-driven.webp",
  },
  {
    id: "resilience",
    number: "03",
    title: "Resilience",
    description:
      "Thrive in adversity and quickly adapt to challenging circumstances.",
    image: "/career-page/7values-resilience.webp",
  },
  {
    id: "integrity",
    number: "04",
    title: "Integrity",
    description:
      "Mistakes will be made and lessons will be learnt. Have a courage to admit it, take proper actions, an do the right things.",
    image: "/career-page/7values-integrity.webp",
  },
  {
    id: "customer-first",
    number: "05",
    title: "Customer First",
    description:
      "A happy customer is a great source of learning. It's essential to understand customers' needs and expectations, while also ensuring their best experience and added value.",
    image: "/career-page/7values-customer-first.webp",
  },
  {
    id: "humble",
    number: "06",
    title: "Humble",
    description:
      "Understanding that everyone is just as good as you. So, take some time to appreciate others, learn continuously, and do good things for others.",
    image: "/career-page/7values-humble.webp",
  },
  {
    id: "teamwork",
    number: "07",
    title: "Teamwork",
    description:
      "Teamwork makes the dream work. Everyone shall always be open for collaboration, embrace diversity, and demonstrate empathy.",
    image: "/career-page/7values-teamwork.webp",
  },
];

export function ValuesCarousel({ cmsData }: ValuesCarouselProps) {
  const content = cmsData.values?.content;
  
  // Use CMS values or fall back to defaults
  const values = content?.values?.length ? content.values : defaultValues;
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % values.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + values.length) % values.length);
  };

  return (
    <div className="w-full min-w-0 max-w-full h-full">
      <h2 
        className="text-2xl sm:text-3xl font-bold mb-4 break-words"
        style={{ 
          color: "#328ee9",
          fontWeight: "bold"
        }}
      >
        {content?.title || "DANA 7 VALUES"}
      </h2>
      
      <div className="bg-white rounded-lg p-4 sm:p-6 flex flex-col min-w-0 h-[calc(100%-2rem)]">
        {/* Image and Text Container */}
        <div className="flex-1 flex flex-col items-center min-w-0 w-full">
          <div className="relative w-full max-w-[min(100%,280px)] sm:w-1/2 sm:max-w-none aspect-square mb-4 mx-auto">
            <img
              src={values[currentIndex].image}
              alt={values[currentIndex].title}
              className="w-full h-full object-contain"
            />
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold self-stretch mb-2 break-words w-full min-w-0">
            {values[currentIndex].title}
          </h2>
          <p className="text-gray-600 text-sm sm:text-base self-stretch break-words w-full min-w-0">
            {values[currentIndex].description}
          </p>
        </div>

        {/* Navigation Controls */}
        <div className="mt-auto pt-4 flex flex-col gap-4">
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full bg-[#164a7a] hover:bg-[#328ee9]/90 border-none text-white"
              onClick={prevSlide}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full bg-[#164a7a] hover:bg-[#328ee9]/90 border-none text-white"
              onClick={nextSlide}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 justify-center">
            {values.map((value, index) => (
              <button
                key={value.id}
                onClick={() => setCurrentIndex(index)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  currentIndex === index
                    ? "bg-[#facc3c] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {value.number} {value.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
